<script lang="ts">
	// @ts-nocheck
	import { Button } from '$lib/components/ui/button';
	import { Input } from '$lib/components/ui/input';
	import { Label } from '$lib/components/ui/label';
	import { Textarea } from '$lib/components/ui/textarea';
	import * as Select from '$lib/components/ui/select';
	import * as Alert from '$lib/components/ui/alert';
	import { CheckCircle, Loader2 } from 'lucide-svelte';
	import { fly } from 'svelte/transition';
	import { trackPhoneClick } from '$lib/utils/analytics';

	const groupSizeOptions = [
		'Just me',
		'2–4 people',
		'5–8 people',
		'9–16 people',
		'17–24 people',
		'More than 24 people'
	];

	let submitted = $state(false);
	let loading = $state(false);
	let failed = $state(false);

	let name = $state('');
	let email = $state('');
	let phone = $state('');
	let groupSize = $state('');
	let travelDates = $state('');
	let message = $state('');
	const type = 'Legends of Tasmania Enquiry';

	function resetForm() {
		name = '';
		email = '';
		phone = '';
		groupSize = '';
		travelDates = '';
		message = '';
	}

	async function handleSubmit(e) {
		e.preventDefault();
		loading = true;
		failed = false;

		try {
			const response = await fetch('/email/send-email', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ type, name, email, phone, groupSize, travelDates, message })
			});

			const result = await response.json();
			if (!response.ok || !result.success) {
				throw new Error(result.error || 'Failed to send email');
			}
			resetForm();
			submitted = true;
		} catch (error) {
			console.error('Error sending email:', error);
			failed = true;
		} finally {
			loading = false;
		}
	}
</script>

<section id="enquiry" class="bg-background py-16 lg:py-24">
	<div class="mx-auto max-w-7xl px-4">
		<div class="mx-auto max-w-2xl text-center">
			<p class="text-sm font-semibold uppercase tracking-widest text-[#194139]">
				Questions about the tour?
			</p>
			<h2 class="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
				Talk to the team who run it
			</h2>
			<p class="mt-4 leading-7 text-gray-700">
				Booking for a group, after a private departure, or just want to know more before you commit?
				Send us a note and we'll come back to you.
			</p>
			<div class="mt-4 flex flex-wrap items-center justify-center">
				<Button variant="link" href="tel:0409404337" onclick={trackPhoneClick}>0409 404 337</Button>
				•
				<Button variant="link" href="mailto:golf@tassietours.com">golf@tassietours.com</Button>
			</div>
		</div>

		<form
			onsubmit={handleSubmit}
			data-sveltekit-preload-data="off"
			class="relative mx-auto mt-8 flex w-full max-w-3xl flex-col gap-8 rounded-3xl bg-secondary p-6 sm:p-10"
		>
			<div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
				<div class="flex flex-col gap-1.5 sm:col-span-2">
					<Label for="legends-name">Name</Label>
					<Input
						required
						type="text"
						id="legends-name"
						placeholder="Name"
						bind:value={name}
						class="bg-white"
					/>
				</div>
				<div class="flex flex-col gap-1.5">
					<Label for="legends-email">Email</Label>
					<Input
						required
						type="email"
						id="legends-email"
						placeholder="email@domain.com"
						bind:value={email}
						class="bg-white"
					/>
				</div>
				<div class="flex flex-col gap-1.5">
					<Label for="legends-phone">Phone</Label>
					<Input
						required
						type="tel"
						id="legends-phone"
						placeholder="Phone number"
						bind:value={phone}
						class="bg-white"
					/>
				</div>
				<div class="flex flex-col gap-1.5">
					<Label>Group size</Label>
					<Select.Root type="single" bind:value={groupSize}>
						<Select.Trigger class="bg-white">{groupSize || 'Select group size'}</Select.Trigger>
						<Select.Content>
							<Select.Group>
								{#each groupSizeOptions as option}
									<Select.Item value={option} label={option}>{option}</Select.Item>
								{/each}
							</Select.Group>
						</Select.Content>
					</Select.Root>
				</div>
				<div class="flex flex-col gap-1.5">
					<Label for="legends-dates">Preferred travel dates</Label>
					<Input
						type="text"
						id="legends-dates"
						placeholder="e.g. March next year"
						bind:value={travelDates}
						class="bg-white"
					/>
				</div>
			</div>

			<div class="flex flex-col gap-1.5">
				<Label for="legends-message">Your message</Label>
				<Textarea
					required
					placeholder="Tell us about your group and what you'd like to know."
					id="legends-message"
					bind:value={message}
					class="bg-white"
				/>
			</div>

			<div class="relative flex h-32 w-full items-center justify-center">
				{#if !submitted}
					<div
						class="absolute inset-0 flex flex-col items-center justify-center gap-3"
						in:fly={{ x: -50, duration: 300 }}
						out:fly={{ x: -50, duration: 300 }}
					>
						{#if !loading}
							<Button
								type="submit"
								class="min-w-40 rounded-full bg-[#194139] p-3 text-center font-semibold text-background transition-all hover:bg-[#194139]/80"
							>
								Send enquiry
							</Button>
						{:else}
							<Button
								disabled
								class="flex min-w-40 rounded-full bg-[#194139] p-3 text-center font-semibold text-background"
							>
								<Loader2 class="h-4 w-4 animate-spin" />
							</Button>
						{/if}
						{#if failed}
							<p class="text-center text-sm text-tt-red">
								Something went wrong sending your enquiry. Please try again or call us on 0409 404
								337.
							</p>
						{/if}
					</div>
				{:else}
					<div
						class="absolute inset-0 mx-auto flex w-full max-w-[500px] items-center justify-center"
						in:fly={{ x: 50, duration: 300, delay: 300 }}
						out:fly={{ x: -50, duration: 300 }}
					>
						<Alert.Root onclick={() => (submitted = false)} class="text-left hover:bg-accent">
							<CheckCircle class="h-4 w-4" />
							<Alert.Title>Your enquiry has been received!</Alert.Title>
							<Alert.Description class="text-wrap">
								Keep an eye out. A member of our team will get back to you within 3 business days.
							</Alert.Description>
						</Alert.Root>
					</div>
				{/if}
			</div>
		</form>
	</div>
</section>
